import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';

export const Login = () => {
  const { login, register } = useAuth();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', full_name: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success('Connexion réussie!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.full_name);
      toast.success('Compte créé avec succès!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{
      backgroundImage: 'radial-gradient(ellipse at top right, rgba(49, 46, 129, 0.1), rgba(248, 250, 252, 1))'
    }}>
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-primary to-primary-foreground" 
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/12e919f1-9a3d-4629-bfe2-795309db9268/images/79df2934f29128dbb7f8b18c85f82d0055a77a9ea44be826028f0a9ebd1263b2.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: '100vh'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 to-primary/60"></div>
        <div className="relative z-10 flex flex-col justify-center items-start p-12 text-white w-full">
          <h1 className="text-4xl font-bold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            InsurBoot
          </h1>
          <p className="text-lg mb-4">Plateforme d'Intelligence Décisionnelle</p>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              <span className="text-sm">Analyse prédictive par IA</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              <span className="text-sm">Tarification dynamique</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
              <span className="text-sm">Pilotage stratégique</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-xl border-border/60" data-testid="login-card">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold tracking-tight text-center flex items-center justify-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <img src="/logo.png" alt="InsurBoot" className="w-9 h-9 object-contain" />
                InsurBoot
              </CardTitle>
              <CardDescription className="text-center text-sm">
                Plateforme d'Intelligence Décisionnelle
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-6">
              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="votre@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Mot de passe</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
                      {loading ? 'Connexion...' : 'Se connecter'}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Nom complet</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="Jean Dupont"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="votre@email.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Mot de passe</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="register-submit-button">
                      {loading ? 'Création...' : 'Créer un compte'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <footer className="border-t border-border/60 bg-card p-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
              <div className="text-center md:text-left">
                <p className="font-semibold text-foreground mb-1">InsurBoot</p>
                <p>11 rue de Verdun, 75008 Paris</p>
                <p>Tél: 09 71 12 11 55</p>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200">
                  🏆 Certifié IA Act
                </Badge>
              </div>
              
              <div className="text-center md:text-right">
                <a href="#" className="hover:text-foreground transition-colors">Mentions légales</a>
                <span className="mx-2">•</span>
                <a href="#" className="hover:text-foreground transition-colors">Politique de confidentialité</a>
              </div>
            </div>
            <div className="mt-4 text-center text-xs text-muted-foreground">
              © {new Date().getFullYear()} InsurBoot. Tous droits réservés.
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};